import React, { useEffect, useState, useRef } from 'react';
import DashboardShell from '@/components/DashboardShell';
import api, { getImageUrl } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Save, Plus, Trash2, UploadCloud, Image as ImageIcon, CreditCard, Settings, Image, DollarSign, Mail, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const TABS = [
  { key: 'general',  label: 'General',        icon: Settings },
  { key: 'banners',  label: 'Hero Banners',    icon: Image },
  { key: 'payments', label: 'Fees & Payments', icon: DollarSign },
  { key: 'email',    label: 'Email',           icon: Mail },
];

export default function Config() {
  const [tab, setTab] = useState('general');
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [addons, setAddons] = useState([]);
  const [uploadingImg, setUploadingImg] = useState(false);
  const fileRef = useRef(null);
  const [heroBanners, setHeroBanners] = useState([]);
  const [savingHero, setSavingHero] = useState(false);
  const [bannerUploadIdx, setBannerUploadIdx] = useState(null);
  const bannerFileRef = useRef(null);
  const bannerTargetRef = useRef(null);
  const [paymentMethods, setPaymentMethods] = useState({ stripe: false, paypal: false });
  const [stripeKey, setStripeKey] = useState('');
  const [paypalClientId, setPaypalClientId] = useState('');
  const [paypalSecret, setPaypalSecret] = useState('');
  const [paypalMode, setPaypalMode] = useState('sandbox');
  const [showStripeKey, setShowStripeKey] = useState(false);
  const [showPaypalSecret, setShowPaypalSecret] = useState(false);

  const fetchConfig = () => {
    setLoading(true);
    api.get('/admin/config')
      .then(r => {
        const c = r.data || {};
        setConfig(c);
        setForm({
          listing_fee: c.listing_fee ?? '',
          featured_fee: c.featured_fee ?? '',
          membership_fee: c.membership_fee ?? '',
          smtp_host: c.smtp_host ?? '',
          smtp_port: c.smtp_port ?? '',
          smtp_user: c.smtp_user ?? '',
          smtp_password: c.smtp_password ?? '',
          from_email: c.from_email ?? '',
          from_name: c.from_name ?? '',
          site_name: c.site_name ?? '',
          contact_email: c.contact_email ?? '',
          auth_image_url: c.auth_image_url ?? '',
        });
        setAddons(c.addon_plans || []);
        setPaymentMethods(c.active_payment_methods || { stripe: false, paypal: false });
        setStripeKey(c.stripe_api_key || '');
        setPaypalClientId(c.paypal_client_id || '');
        setPaypalSecret(c.paypal_client_secret || '');
        setPaypalMode(c.paypal_mode || 'sandbox');
      })
      .catch(() => toast.error('Failed to load configuration'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchConfig(); }, []);
  useEffect(() => {
    api.get('/admin/hero-banners').then(r => setHeroBanners(r.data.banners || [])).catch(() => {});
  }, []);

  const addBanner    = () => setHeroBanners(p => [...p, { id: '', image_url: '', heading: '', primary_label: 'Explore Now', primary_link: '/browse', secondary_label: 'List Now', secondary_link: '/register' }]);
  const removeBanner = i  => setHeroBanners(p => p.filter((_, idx) => idx !== i));
  const updateBanner = (i, k, v) => setHeroBanners(p => p.map((b, idx) => idx === i ? { ...b, [k]: v } : b));
  const moveBanner   = (i, d) => setHeroBanners(p => {
    const j = i + d; if (j < 0 || j >= p.length) return p;
    const a = [...p]; [a[i], a[j]] = [a[j], a[i]]; return a;
  });

  const handleBannerImageUpload = async (e) => {
    const file = e.target.files?.[0]; const idx = bannerTargetRef.current;
    if (!file || idx === null || idx === undefined) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be under 10MB'); return; }
    setBannerUploadIdx(idx);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      updateBanner(idx, 'image_url', r.data.url);
      toast.success('Image uploaded — remember to save banners');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setBannerUploadIdx(null); if (bannerFileRef.current) bannerFileRef.current.value = ''; }
  };

  const saveHeroBanners = async () => {
    if (heroBanners.some(b => !b.image_url || !b.heading.trim())) { toast.error('Each banner needs an image and a heading'); return; }
    setSavingHero(true);
    try {
      const r = await api.put('/admin/hero-banners', { banners: heroBanners });
      setHeroBanners(r.data.banners || []); toast.success('Hero banners saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save banners');
    } finally { setSavingHero(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/config', {
        ...form,
        active_payment_methods: paymentMethods,
        stripe_api_key: stripeKey || undefined,
        paypal_client_id: paypalClientId || undefined,
        paypal_client_secret: paypalSecret || undefined,
        paypal_mode: paypalMode,
      });
      await api.put('/admin/config/addon-plans', { plans: addons });
      toast.success('Configuration saved'); fetchConfig();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save configuration');
    } finally { setSaving(false); }
  };

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be under 10MB'); return; }
    setUploadingImg(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setField('auth_image_url', r.data.url); toast.success('Image uploaded — remember to save');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploadingImg(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const addAddon    = () => setAddons(p => [...p, { name: '', price: 0, description: '' }]);
  const removeAddon = i  => setAddons(p => p.filter((_, idx) => idx !== i));
  const setAddon    = (i, k, v) => setAddons(p => p.map((a, idx) => idx === i ? { ...a, [k]: v } : a));

  const F = ({ label, name, type = 'text', placeholder = '' }) => (
    <div>
      <label className="block text-xs font-medium mb-1.5 uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>{label}</label>
      <Input type={type} value={form[name] ?? ''} onChange={e => setField(name, type === 'number' ? Number(e.target.value) : e.target.value)}
        placeholder={placeholder} className="text-sm h-10" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }} data-testid={`config-field-${name}`} />
    </div>
  );

  const Toggle = ({ label, desc, value, onToggle, testId, badge }) => (
    <div className="flex items-center justify-between p-4 rounded-xl border" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--bg-subtle)' }}>
      <div className="flex-1 mr-4">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{label}</p>
          {value && badge && <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide" style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}>{badge}</span>}
        </div>
        <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{desc}</p>
      </div>
      <button type="button" data-testid={testId} onClick={onToggle}
        className="relative inline-flex items-center flex-shrink-0 h-6 w-11 rounded-full transition-colors duration-200 focus:outline-none"
        style={{ backgroundColor: value ? 'var(--text)' : 'var(--border-clr)' }} aria-pressed={value}>
        <span className="inline-block w-4 h-4 rounded-full bg-white shadow transition-transform duration-200"
          style={{ transform: value ? 'translateX(1.375rem)' : 'translateX(0.125rem)' }} />
      </button>
    </div>
  );

  const SaveBar = ({ onClick, disabled, label = 'Save changes' }) => (
    <div className="flex justify-end pt-2 border-t" style={{ borderColor: 'var(--border-clr)' }}>
      <Button onClick={onClick} disabled={disabled}
        style={{ backgroundColor: 'var(--text)', color: 'var(--bg)', borderRadius: '10px' }} data-testid="config-save-button">
        <Save size={14} className="mr-2" />{disabled ? 'Saving…' : label}
      </Button>
    </div>
  );

  return (
    <DashboardShell title="Configuration">
      {/* Tab bar */}
      <div className="flex gap-0 mb-8 border-b" style={{ borderColor: 'var(--border-clr)' }}>
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`config-tab-${t.key}`}
              className="flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors relative"
              style={{ color: active ? 'var(--text)' : 'var(--text-muted)', borderBottom: active ? '2px solid var(--text)' : '2px solid transparent', marginBottom: '-1px', backgroundColor: 'transparent' }}>
              <Icon size={15} />{t.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-12 rounded-xl animate-pulse" style={{ backgroundColor: 'var(--border-clr)' }} />)}</div>
      ) : (
        <div className="max-w-2xl space-y-6">

          {/* ── GENERAL ─────────────────────────────────────────── */}
          {tab === 'general' && <>
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: 'var(--text-muted)' }}>Platform</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5 rounded-xl border" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}>
                <F label="Site name" name="site_name" placeholder="The Listing Room" />
                <F label="Contact email" name="contact_email" placeholder="hello@example.com" />
              </div>
            </div>

            <div>
              <h3 className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: 'var(--text-muted)' }}>Login &amp; Register image</h3>
              <div className="flex flex-col sm:flex-row gap-5 p-5 rounded-xl border" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}>
                <div className="rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center" style={{ width: '160px', height: '210px', border: '1px solid var(--border-clr)', backgroundColor: 'var(--bg-subtle)' }}>
                  {form.auth_image_url
                    ? <img src={getImageUrl(form.auth_image_url)} alt="Auth preview" className="w-full h-full object-cover" />
                    : <div className="text-center px-4"><ImageIcon size={20} className="mx-auto mb-2" style={{ color: 'var(--text-muted)' }} /><p className="text-xs" style={{ color: 'var(--text-muted)' }}>Default</p></div>}
                </div>
                <div className="flex-1 space-y-3">
                  <p className="text-xs p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-subtle)', color: 'var(--text-muted)', border: '1px solid var(--border-clr)' }}>
                    <strong style={{ color: 'var(--text)' }}>1200 × 1600px recommended</strong> — portrait, 3:4 ratio. Fills the left panel of login &amp; register pages. JPG, PNG or WebP · max 10MB.
                  </p>
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleImageUpload} className="hidden" />
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" variant="outline" disabled={uploadingImg} onClick={() => fileRef.current?.click()} style={{ borderRadius: '10px', borderColor: 'var(--border-strong)' }}>
                      <UploadCloud size={14} className="mr-2" />{uploadingImg ? 'Uploading…' : 'Upload image'}
                    </Button>
                    {form.auth_image_url && (
                      <Button type="button" variant="ghost" onClick={() => setField('auth_image_url', '')} style={{ borderRadius: '10px', color: 'var(--danger)' }}>
                        <Trash2 size={14} className="mr-2" />Reset to default
                      </Button>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1.5 uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Or paste URL</label>
                    <Input value={form.auth_image_url ?? ''} onChange={e => setField('auth_image_url', e.target.value)} placeholder="https://…" className="text-sm h-10" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--bg-subtle)' }} />
                  </div>
                </div>
              </div>
            </div>
            <SaveBar onClick={handleSave} disabled={saving} />
          </>}

          {/* ── HERO BANNERS ─────────────────────────────────────── */}
          {tab === 'banners' && <>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>Landing page hero</h3>
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>These images rotate as the homepage hero carousel.</p>
              </div>
              <Button type="button" variant="outline" onClick={addBanner} style={{ borderRadius: '10px', borderColor: 'var(--border-strong)' }} data-testid="add-banner-button">
                <Plus size={14} className="mr-1.5" />Add banner
              </Button>
            </div>

            <p className="text-xs px-3 py-2.5 rounded-lg" style={{ backgroundColor: 'var(--bg-subtle)', border: '1px solid var(--border-clr)', color: 'var(--text-muted)' }}>
              <strong style={{ color: 'var(--text)' }}>1920 × 1080px recommended</strong> — landscape, 16:9. JPG, PNG or WebP · max 10MB.
            </p>

            <input ref={bannerFileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleBannerImageUpload} className="hidden" />

            {heroBanners.length === 0 && (
              <div className="py-12 text-center rounded-xl border border-dashed" style={{ borderColor: 'var(--border-clr)' }}>
                <ImageIcon size={28} className="mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No banners yet — click "Add banner" to get started.</p>
              </div>
            )}

            <div className="space-y-4">
              {heroBanners.map((b, i) => (
                <div key={i} className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }} data-testid={`banner-row-${i}`}>
                  <div className="flex items-center justify-between px-4 py-2.5 border-b" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--bg-subtle)' }}>
                    <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>Banner {i + 1}</span>
                    <div className="flex items-center gap-1">
                      <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-xs" disabled={i === 0} onClick={() => moveBanner(i, -1)}>↑</Button>
                      <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-xs" disabled={i === heroBanners.length - 1} onClick={() => moveBanner(i, 1)}>↓</Button>
                      <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeBanner(i)} style={{ color: 'var(--danger)' }} data-testid={`banner-remove-${i}`}><Trash2 size={14} /></Button>
                    </div>
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="flex gap-4">
                      <div className="rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center" style={{ width: '140px', height: '80px', border: '1px solid var(--border-clr)', backgroundColor: 'var(--bg-subtle)' }}>
                        {b.image_url ? <img src={getImageUrl(b.image_url)} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={18} style={{ color: 'var(--text-muted)' }} />}
                      </div>
                      <div className="flex-1 space-y-2">
                        <Button type="button" variant="outline" size="sm" disabled={bannerUploadIdx === i}
                          onClick={() => { bannerTargetRef.current = i; bannerFileRef.current?.click(); }}
                          style={{ borderRadius: '8px', borderColor: 'var(--border-strong)' }} data-testid={`banner-upload-${i}`}>
                          <UploadCloud size={13} className="mr-1.5" />{bannerUploadIdx === i ? 'Uploading…' : 'Upload'}
                        </Button>
                        <Input value={b.image_url} onChange={e => updateBanner(i, 'image_url', e.target.value)} placeholder="Image URL" className="text-sm h-9" style={{ borderColor: 'var(--border-clr)' }} data-testid={`banner-image-url-${i}`} />
                      </div>
                    </div>
                    <Input value={b.heading} onChange={e => updateBanner(i, 'heading', e.target.value)} placeholder="Heading text" className="text-sm" style={{ borderColor: 'var(--border-clr)' }} data-testid={`banner-heading-${i}`} />
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={b.primary_label} onChange={e => updateBanner(i, 'primary_label', e.target.value)} placeholder="Primary label" className="text-sm h-9" style={{ borderColor: 'var(--border-clr)' }} />
                      <Input value={b.primary_link} onChange={e => updateBanner(i, 'primary_link', e.target.value)} placeholder="/browse" className="text-sm h-9" style={{ borderColor: 'var(--border-clr)' }} />
                      <Input value={b.secondary_label} onChange={e => updateBanner(i, 'secondary_label', e.target.value)} placeholder="Secondary label" className="text-sm h-9" style={{ borderColor: 'var(--border-clr)' }} />
                      <Input value={b.secondary_link} onChange={e => updateBanner(i, 'secondary_link', e.target.value)} placeholder="/register" className="text-sm h-9" style={{ borderColor: 'var(--border-clr)' }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <SaveBar onClick={saveHeroBanners} disabled={savingHero} label="Save banners" />
          </>}

          {/* ── FEES & PAYMENTS ──────────────────────────────────── */}
          {tab === 'payments' && <>
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: 'var(--text-muted)' }}>Listing fees</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-5 rounded-xl border" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}>
                <F label="Listing fee (£)" name="listing_fee" type="number" placeholder="0" />
                <F label="Featured fee (£)" name="featured_fee" type="number" placeholder="0" />
                <F label="Membership fee (£)" name="membership_fee" type="number" placeholder="0" />
              </div>
            </div>

            <div>
              <h3 className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: 'var(--text-muted)' }}>Payment gateways</h3>
              <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>Enabled gateways appear at checkout when a member publishes a listing.</p>
              <div className="space-y-3">

                {/* Stripe */}
                <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}>
                  <div className="flex items-center justify-between p-4">
                    <div className="flex-1 mr-4">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Stripe</p>
                        {paymentMethods.stripe && <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide" style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}>Active</span>}
                      </div>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>Card payments via Stripe Checkout</p>
                    </div>
                    <button type="button" data-testid="toggle-stripe" onClick={() => setPaymentMethods(p => ({ ...p, stripe: !p.stripe }))}
                      className="relative inline-flex items-center flex-shrink-0 h-6 w-11 rounded-full transition-colors duration-200 focus:outline-none"
                      style={{ backgroundColor: paymentMethods.stripe ? 'var(--text)' : 'var(--border-clr)' }} aria-pressed={paymentMethods.stripe}>
                      <span className="inline-block w-4 h-4 rounded-full bg-white shadow transition-transform duration-200"
                        style={{ transform: paymentMethods.stripe ? 'translateX(1.375rem)' : 'translateX(0.125rem)' }} />
                    </button>
                  </div>
                  <div className="px-4 pb-4 pt-0 border-t" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--bg-subtle)' }}>
                    <label className="block text-xs font-medium mb-1.5 mt-3 uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Secret key</label>
                    <div className="relative">
                      <Input type={showStripeKey ? 'text' : 'password'} value={stripeKey}
                        onChange={e => setStripeKey(e.target.value)}
                        placeholder="sk_live_… or sk_test_…"
                        className="text-sm h-10 pr-10" style={{ borderColor: 'var(--border-clr)', fontFamily: 'monospace' }} data-testid="stripe-key-input" />
                      <button type="button" onClick={() => setShowStripeKey(v => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-70" style={{ color: 'var(--text-muted)' }}>
                        {showStripeKey ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                    <p className="text-xs mt-1.5" style={{ color: 'var(--text-muted)' }}>Leave blank to keep existing key. Use <code style={{ fontSize: '11px' }}>sk_test_emergent</code> for the pre-configured test key.</p>
                  </div>
                </div>

                {/* PayPal */}
                <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}>
                  <div className="flex items-center justify-between p-4">
                    <div className="flex-1 mr-4">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>PayPal</p>
                        {paymentMethods.paypal && <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide" style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}>Active</span>}
                      </div>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>PayPal Checkout buttons</p>
                    </div>
                    <button type="button" data-testid="toggle-paypal" onClick={() => setPaymentMethods(p => ({ ...p, paypal: !p.paypal }))}
                      className="relative inline-flex items-center flex-shrink-0 h-6 w-11 rounded-full transition-colors duration-200 focus:outline-none"
                      style={{ backgroundColor: paymentMethods.paypal ? 'var(--text)' : 'var(--border-clr)' }} aria-pressed={paymentMethods.paypal}>
                      <span className="inline-block w-4 h-4 rounded-full bg-white shadow transition-transform duration-200"
                        style={{ transform: paymentMethods.paypal ? 'translateX(1.375rem)' : 'translateX(0.125rem)' }} />
                    </button>
                  </div>
                  <div className="px-4 pb-4 pt-0 border-t space-y-3" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--bg-subtle)' }}>
                    <div>
                      <label className="block text-xs font-medium mb-1.5 mt-3 uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Client ID</label>
                      <Input value={paypalClientId} onChange={e => setPaypalClientId(e.target.value)}
                        placeholder="AXxxxxxxxxxxxx…"
                        className="text-sm h-10" style={{ borderColor: 'var(--border-clr)', fontFamily: 'monospace' }} data-testid="paypal-client-id-input" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1.5 uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Client secret</label>
                      <div className="relative">
                        <Input type={showPaypalSecret ? 'text' : 'password'} value={paypalSecret}
                          onChange={e => setPaypalSecret(e.target.value)}
                          placeholder="EXxxxxxxxxxxxx…"
                          className="text-sm h-10 pr-10" style={{ borderColor: 'var(--border-clr)', fontFamily: 'monospace' }} data-testid="paypal-secret-input" />
                        <button type="button" onClick={() => setShowPaypalSecret(v => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-70" style={{ color: 'var(--text-muted)' }}>
                          {showPaypalSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1.5 uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Mode</label>
                      <div className="flex gap-2">
                        {['sandbox', 'live'].map(m => (
                          <button key={m} type="button" onClick={() => setPaypalMode(m)}
                            className="px-4 py-1.5 text-xs font-medium rounded-lg border transition-colors capitalize"
                            style={{ borderColor: paypalMode === m ? 'var(--text)' : 'var(--border-clr)', backgroundColor: paypalMode === m ? 'var(--text)' : 'transparent', color: paypalMode === m ? 'var(--bg)' : 'var(--text-muted)' }}>
                            {m}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>Add-on plans</h3>
                <Button variant="ghost" size="sm" onClick={addAddon} className="gap-1.5 h-7 text-xs" style={{ color: 'var(--text)' }}>
                  <Plus size={13} />Add plan
                </Button>
              </div>
              {addons.length === 0 ? (
                <div className="py-8 text-center rounded-xl border border-dashed" style={{ borderColor: 'var(--border-clr)' }}>
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No add-on plans configured yet.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {addons.map((a, i) => (
                    <div key={i} className="flex gap-3 items-start p-4 rounded-xl border" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 flex-1">
                        <div>
                          <label className="block text-xs font-medium mb-1.5 uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Name</label>
                          <Input value={a.name} onChange={e => setAddon(i, 'name', e.target.value)} placeholder="Plan name" className="text-sm h-10" style={{ borderColor: 'var(--border-clr)' }} />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1.5 uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Price (£)</label>
                          <Input type="number" value={a.price} onChange={e => setAddon(i, 'price', Number(e.target.value))} placeholder="0" className="text-sm h-10" style={{ borderColor: 'var(--border-clr)' }} />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1.5 uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Description</label>
                          <Input value={a.description} onChange={e => setAddon(i, 'description', e.target.value)} placeholder="Brief description" className="text-sm h-10" style={{ borderColor: 'var(--border-clr)' }} />
                        </div>
                      </div>
                      <button onClick={() => removeAddon(i)} className="mt-7 p-1.5 rounded-lg" style={{ color: 'var(--danger)' }}><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <SaveBar onClick={handleSave} disabled={saving} />
          </>}

          {/* ── EMAIL ────────────────────────────────────────────── */}
          {tab === 'email' && <>
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: 'var(--text-muted)' }}>SMTP server</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5 rounded-xl border" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}>
                <F label="SMTP host" name="smtp_host" placeholder="smtp.gmail.com" />
                <F label="SMTP port" name="smtp_port" type="number" placeholder="587" />
                <F label="SMTP username" name="smtp_user" placeholder="user@gmail.com" />
                <F label="SMTP password" name="smtp_password" type="password" placeholder="••••••••" />
              </div>
            </div>

            <div>
              <h3 className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: 'var(--text-muted)' }}>Sender identity</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5 rounded-xl border" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}>
                <F label="From email" name="from_email" placeholder="noreply@listingroom.com" />
                <F label="From name" name="from_name" placeholder="The Listing Room" />
              </div>
            </div>

            <SaveBar onClick={handleSave} disabled={saving} />
          </>}

        </div>
      )}
    </DashboardShell>
  );
}
